import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
  clamp,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} galleryViewport - The zoom gallery viewport element.
 * @property {HTMLElement} gallery - The zoom gallery track element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'galleryViewport', 'gallery', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());
  /** @type {HTMLElement | null} */
  #scrollContainer = null;
  /** @type {AbortController | null} */
  #swipeController = null;
  /** @type {number} */
  #currentIndex = 0;
  /** @type {number} */
  #galleryTransform = 0;
  /** @type {boolean} */
  #swipeMoved = false;

  connectedCallback() {
    super.connectedCallback();
    this.#scrollContainer = this.#getScrollContainer();
    this.#scrollContainer?.addEventListener('scroll', this.handleScroll, { passive: true });
    this.#initGallerySwipe();
    window.addEventListener('resize', this.#handleResize, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#scrollContainer?.removeEventListener('scroll', this.handleScroll);
    this.#scrollContainer = null;
    this.#swipeController?.abort();
    this.#swipeController = null;
    window.removeEventListener('resize', this.#handleResize);
  }

  #handleResize = debounce(() => {
    if (!this.refs.dialog.open || !this.#isHorizontalGallery()) return;

    this.#updateSlideWidth();
    this.#goToIndex(this.#currentIndex, false);
  }, 100);

  /**
   * @returns {HTMLElement}
   */
  #getScrollContainer() {
    if (this.#isHorizontalGallery()) {
      return this.refs.gallery;
    }

    return this.refs.gallery;
  }

  #isHorizontalGallery() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  /**
   * @returns {number}
   */
  #updateSlideWidth() {
    const { galleryViewport } = this.refs;
    if (!galleryViewport) return 0;

    const width = galleryViewport.clientWidth;
    if (width > 0) {
      galleryViewport.style.setProperty('--zoom-slide-width', `${width}px`);
    }

    return width;
  }

  #isActiveSlideZoomed() {
    const slide = this.refs.media[this.#currentIndex];
    const wrapper = slide?.querySelector('drag-zoom-wrapper');
    return wrapper?.classList.contains('is-zoomed') ?? false;
  }

  #initGallerySwipe() {
    const { gallery } = this.refs;
    if (!gallery) return;

    this.#swipeController?.abort();
    this.#swipeController = new AbortController();
    const { signal } = this.#swipeController;

    gallery.addEventListener('pointerdown', this.#onGalleryPointerDown, { signal });
    gallery.addEventListener('click', this.#onGalleryClick, { signal, capture: true });
  }

  #onGalleryClick = (event) => {
    if (!this.#swipeMoved) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#swipeMoved = false;
  };

  #onGalleryPointerDown = (event) => {
    if (!this.#isHorizontalGallery()) return;
    if (this.refs.media.length <= 1) return;
    if (this.#isActiveSlideZoomed()) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('.product-media-container')) return;

    const { gallery, media } = this.refs;
    const slideWidth = this.#updateSlideWidth() || gallery.parentElement?.clientWidth || gallery.clientWidth;
    if (!slideWidth) return;

    const startX = event.clientX;
    const startY = event.clientY;
    const startOffset = this.#galleryTransform;
    let moved = false;
    let lastX = startX;

    const controller = new AbortController();
    const { signal } = controller;

    const onPointerMove = (moveEvent) => {
      const dx = moveEvent.clientX - startX;
      const dy = moveEvent.clientY - startY;

      if (!moved) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;

        if (Math.abs(dy) > Math.abs(dx)) {
          controller.abort();
          return;
        }

        moved = true;
        this.#swipeMoved = true;
        gallery.setPointerCapture(moveEvent.pointerId);
        gallery.classList.add('is-dragging');
      }

      moveEvent.preventDefault();
      lastX = moveEvent.clientX;

      const dragDx = moveEvent.clientX - startX;
      let nextOffset = startOffset + dragDx;
      const minOffset = -(media.length - 1) * slideWidth;
      const maxOffset = 0;

      if (nextOffset > maxOffset) {
        nextOffset = maxOffset + (nextOffset - maxOffset) * 0.35;
      } else if (nextOffset < minOffset) {
        nextOffset = minOffset + (nextOffset - minOffset) * 0.35;
      }

      gallery.style.transition = 'none';
      gallery.style.transform = `translate3d(${nextOffset}px, 0, 0)`;
    };

    const onPointerEnd = () => {
      controller.abort();
      gallery.classList.remove('is-dragging');

      if (!moved) return;

      const dx = lastX - startX;
      let targetIndex = this.#currentIndex;
      const swipeThreshold = Math.min(slideWidth * 0.18, 56);

      if (dx <= -swipeThreshold && targetIndex < media.length - 1) {
        targetIndex += 1;
      } else if (dx >= swipeThreshold && targetIndex > 0) {
        targetIndex -= 1;
      }

      this.#goToIndex(targetIndex, true);
    };

    gallery.addEventListener('pointermove', onPointerMove, { signal, passive: false });
    gallery.addEventListener('pointerup', onPointerEnd, { signal });
    gallery.addEventListener('pointercancel', onPointerEnd, { signal });
  };

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    const open = () => {
      dialog.showModal();

      if (this.#isHorizontalGallery()) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.#updateSlideWidth();
            this.#goToIndex(index, false);
          });
        });
      } else {
        for (const target of [targetThumbnail, targetImage]) {
          target?.scrollIntoView({ behavior: 'instant' });
        }
      }
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');

    if (this.#isHorizontalGallery()) {
      this.#goToIndex(index, false);
    } else {
      this.selectThumbnail(index, { behavior: 'instant' });
    }
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   */
  handleScroll = debounce(async () => {
    if (this.#isHorizontalGallery()) return;

    const { media, gallery } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media, gallery);
    const activeIndex = media.indexOf(mostVisibleElement);
    if (activeIndex < 0) return;

    this.#currentIndex = activeIndex;
    this.#syncThumbnailState(activeIndex);
    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media, gallery } = this.refs;

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    const activeIndex = this.#isHorizontalGallery()
      ? this.#currentIndex
      : media.indexOf(await getMostVisibleElement(media, gallery));
    const mostVisibleElement = media[activeIndex];
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide || !mostVisibleElement) return this.closeDialog();
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    const { dialog, gallery } = this.refs;
    dialog.close();
    gallery.classList.remove('is-dragging');
    gallery.style.transition = '';
    gallery.style.transform = '';
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * @param {number} index
   */
  #syncThumbnailState(index) {
    const { thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
      block: 'center',
      inline: 'center',
    });
  }

  /**
   * @param {number} index
   * @param {boolean} animate
   */
  #goToIndex(index, animate = true) {
    const { gallery, media } = this.refs;
    if (!gallery || !media.length) return;

    const nextIndex = clamp(index, 0, media.length - 1);
    const slideWidth = this.#updateSlideWidth() || gallery.parentElement?.clientWidth || 0;
    const offset = slideWidth ? -nextIndex * slideWidth : 0;

    this.#currentIndex = nextIndex;
    this.#galleryTransform = offset;

    gallery.classList.toggle('is-dragging', !animate);
    gallery.style.transition =
      animate && !prefersReducedMotion() ? 'transform 0.32s cubic-bezier(0.25, 1, 0.5, 1)' : 'none';
    gallery.style.transform = `translate3d(${offset}px, 0, 0)`;

    this.#syncThumbnailState(nextIndex);

    const targetImage = media[nextIndex];
    if (targetImage) {
      this.loadHighResolutionImage(targetImage);
    }

    this.dispatchEvent(new ZoomMediaSelectedEvent(nextIndex));
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    if (this.#isHorizontalGallery()) {
      this.#goToIndex(index, options.behavior !== 'instant');
      return;
    }

    const { media, thumbnails, gallery } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    this.#currentIndex = index;
    this.#syncThumbnailState(index);

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @param {HTMLElement | null | undefined} root - Optional scroll container root.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements, root) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        root: root ?? null,
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
